import { EUserType, TWebinarStatus } from '@src/types';

export const WEBINAR_DURATION_VALUES = [
  { label: '15 mins', value: '15' },
  { label: '30 mins', value: '30' },
  { label: '45 mins', value: '45' },
  { label: '1 hr', value: '60' },
  { label: '1 hr 15 mins', value: '75' },
  { label: '1 hr 30 mins', value: '90' },
  { label: '1 hr 45 mins', value: '105' },
  { label: '2 hrs', value: '120' },
  { label: '2 hrs 15 mins', value: '135' },
  { label: '2 hrs 30 mins', value: '150' },
  { label: '2 hrs 45 mins', value: '165' },
  { label: '3 hrs', value: '180' },
  { label: '3 hrs 15 mins', value: '195' },
  { label: '3 hrs 30 mins', value: '210' },
  { label: '3 hrs 45 mins', value: '225' },
  { label: '4 hrs', value: '240' },
  { label: '4 hrs 15 mins', value: '255' },
  { label: '4 hrs 30 mins', value: '270' },
  { label: '4 hrs 45 mins', value: '285' },
  { label: '5 hrs', value: '300' },
  { label: '5 hrs 15 mins', value: '315' },
  { label: '5 hrs 30 mins', value: '330' },
  { label: '5 hrs 45 mins', value: '345' },
  { label: '6 hrs', value: '360' },
];

export enum EWebinarRoutes {
  CLOSED = '/webinar/closed/:roomTitle',
  FULL = '/webinar/full',
  CREATE = '/webinar/create',
  UPDATE = '/webinar/update',
  REGISTRATION_FORM = '/webinar/registration-form',
  JOIN = '/webinar/join',
  SUCCESS = '/webinar/success',
  ALREADY_REGISTERED = '/webinar/already-registered',
  ADMIN_WEBINAR = 'webinar/:id/attendee',
  DECLINE_INVITATION = '/webinar/:webinarId/invitation/decline',
  WEBINAR_FULL_REGISTRATION = '/webinar/:webinarId/full-registration',
  END = '/end',
  REJOIN = '/webinar/rejoin',
  WEBINAR_FULL_ROOM = '/webinar/full-room',
  WEBINAR_EXPIRED = '/webinar/expired',
  RESTRICTED_ACCESS = '/webinar/restricted-access',
  ROOM_NOT_FOUND = '/webinar/room-not-found',
}

export enum EWebinarSockets {
  JOIN = 'join',
  VIEWER_JOIN = 'viewer-join',
  STREAM_START = 'stream-start',
  HOST_STREAM_START = 'host-stream-start',
  HOST_STREAM_STOP = 'host-stream-stop',
  ATTENDEE_DETAILS = 'attendee-details',
  END_WEBINAR = 'endWebinar',
  EXIT_ROOM = 'exitRoom',
  EVENT = 'event',
  REMOVE_ME = 'removeMe',
  JOIN_LOBBY = 'joinLobby',
  GET_POLLS = 'getPolls',
  UPDATE_PEER_INFO = 'updatePeerInfo',
  VIEWERS = 'viewers',
  GET_CHAT_MESSAGES = 'getChatMessages',
  VIEWER_IFRAME_LAYOUT = 'viewerIframeLayout',
  VOICE_REPORTER_UPDATE = 'voiceReporterUpdate',
  VOLUME_INDICATOR = 'volumeIndicator',
  DISCONNECTING = 'disconnecting',
  DISCONNECT = 'disconnect',
  CONNECT = 'connect',
}

type TWebinarRoleModalType = {
  [K in EUserType]?: {
    [J in EUserType]?: {
      title: string;
      desc: string;
    };
  };
};

export const WEBINAR_ROLE_MODAL: TWebinarRoleModalType = {
  [EUserType.ATTENDEE]: {
    [EUserType.CO_HOST]: {
      title: 'Promote webinar Attendee to Co-host',
      desc:
        'The attendee will be upgraded to a co-host with access to co-host tools. ' +
        'Their original attendee link will redirect to the co-host view. ' +
        'By confirming, you’ll be able to send them the updated invitation.',
    },
    [EUserType.SPEAKER]: {
      title: 'Promote webinar Attendee to Speaker',
      desc:
        'The attendee will be granted speaker permissions immediately. ' +
        'Their existing attendee link will still work and redirect to the speaker view. ' +
        'By confirming, you’ll be able to send them the updated invitation.',
    },
  },
  [EUserType.CO_HOST]: {
    [EUserType.ATTENDEE]: {
      title: 'Change webinar Co-host to Attendee',
      desc:
        'The co-host will be downgraded to a regular attendee. ' +
        'Their current co-host link will redirect to the attendee view. ' +
        'By confirming, you’ll be able to send them the updated attendee invitation.',
    },
    [EUserType.SPEAKER]: {
      title: 'Change webinar Co-host to Speaker',
      desc:
        'The co-host will be granted speaker permissions immediately. ' +
        'Their existing co-host link will still work and redirect to the speaker view. ' +
        'By confirming, you’ll be able to send them the updated speaker invitation.',
    },
  },
  [EUserType.SPEAKER]: {
    [EUserType.ATTENDEE]: {
      title: 'Change webinar Speaker to Attendee',
      desc:
        'The speaker will be downgraded to a regular attendee. ' +
        'Their existing speaker link will still work and redirect to the attendee view. ' +
        'By confirming, you’ll be able to send them the updated attendee invitation.',
    },
    [EUserType.CO_HOST]: {
      title: 'Change webinar Speaker to Co-host',
      desc:
        'The speaker will be upgraded to a co-host with access to co-host tools. ' +
        'Their current link will redirect to the co-host view. ' +
        'By confirming, you’ll be able to send them the updated co-host invitation.',
    },
  },
};

export const PACKAGE_LIMIT_MESSAGE = "You've reached your package limit, Upgrade your plan to continue.";

export const PANEL_LIST_CAPACITY_MESSAGE = 'You’ve reached your panel list capacity';

export const WEBINAR_STARTED_CHANGE_DISABLED = 'Change are not allowed as the webinar has started.';

export const STATUS_VARIANTS: Record<TWebinarStatus, 'approve' | 'success' | 'danger'> = {
  PENDING: 'approve',
  APPROVED: 'success',
  REJECTED: 'danger',
};

export const STATUS_LABELS: Record<TWebinarStatus, string> = {
  PENDING: 'Approve',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};
export const FALLBACK_TIMEZONES = [
  'Africa/Abidjan',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'America/Chicago',
  'America/New_York',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Sao_Paulo',
  'Asia/Amman',
  'Asia/Baghdad',
  'Asia/Dubai',
  'Asia/Jerusalem',
  'Asia/Kolkata',
  'Asia/Riyadh',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Europe/Amsterdam',
  'Europe/Berlin',
  'Europe/Istanbul',
  'Europe/London',
  'Europe/Moscow',
  'Pacific/Auckland',
  'Pacific/Honolulu',
  'UTC',
];

export const MEETING_TYPE = {
  WEBINAR: 'Webinar',
  MEETING: 'Meeting',
};

export const CURRENT_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
